import {
  getApiErrorDetailsRecord,
  getApiErrorMessage,
  getApiErrorStatus,
} from "../apiErrors";
import type {
  DataStoryRecipeId,
  StoryDetailsValues,
} from "./dataStoryFlow.types";
import type {
  CreateStoryRequest,
  CreateStoryResponse,
  StoryCreationMethod,
} from "./storiesApi.types";

/**
 * Recipe ids that are creation *methods* rather than templates. Anything else
 * in the picker is a template and travels as `template_id`.
 */
const METHOD_BY_RECIPE: Partial<Record<DataStoryRecipeId, StoryCreationMethod>> =
  {
    "blank-canvas": "blank_canvas",
    ai: "ai",
  };

export interface CreateRequestOptions {
  /**
   * The tenant creates stories through api-service (`new_path`). Its blank
   * canvas is a story that is ready on creation, with no plan to review, so it
   * travels as `blank` rather than as the old path's details-driven
   * `blank_canvas`.
   */
  newPath?: boolean;
}

export const creationMethodFor = (
  recipeId: DataStoryRecipeId,
  { newPath = false }: CreateRequestOptions = {},
): StoryCreationMethod => {
  if (newPath && recipeId === "blank-canvas") return "blank";
  return METHOD_BY_RECIPE[recipeId] ?? "template";
};

/**
 * Flow values → `POST /stories` body.
 *
 * `details` is optional because a caller may build a request with no details
 * step at all (a bare test, or a method whose frame is not designed yet), which
 * is why every details field is optional in the request. Empty strings are dropped rather than sent, so the backend's own
 * defaults apply instead of a blank title.
 *
 * One mapper for every details step rather than one each: they share the goal,
 * and the fields they do not share are read behind an `in` check — which is why
 * the agent frame needed no new file, only the `audiences` guard below.
 *
 * The transient UI state (`audienceDraft`, `isAddingAudience`, `isAddingSource`,
 * `isModelOpen`) is dropped here — this is the boundary where UI state stops
 * and payload begins.
 */
export const toCreateStoryRequest = (
  recipeId: DataStoryRecipeId,
  details?: StoryDetailsValues,
  options: CreateRequestOptions = {},
): CreateStoryRequest => {
  const method = creationMethodFor(recipeId, options);

  const request: CreateStoryRequest = { creation_method: method };
  if (method === "template") request.template_id = recipeId;

  if (!details) return request;

  // A blank story has no goal to plan from, and the API refuses (422) one that
  // arrives with a prompt or a goal. Only its name can travel.
  if (method === "blank") {
    const title = "name" in details ? details.name.trim() : "";
    if (title) request.title = title;
    return request;
  }

  const goal = details.goal.trim();
  if (goal) request.goal = goal;

  // Not the agent frame: an agent carries its own audience, so that frame draws
  // no audience field and its values have no `audiences` key at all.
  if ("audiences" in details && details.audiences.length) {
    request.audiences = details.audiences;
  }

  // Blank-canvas only: the AI frame draws no name field, so the backend titles
  // those stories from the goal/prompt instead.
  if ("name" in details) {
    const title = details.name.trim();
    if (title) request.title = title;
  }

  // AI and agent frames. The two tabs are mutually exclusive, so exactly one of `sources` /
  // `data_model_id` travels — and neither when the user left the tab untouched,
  // which the backend reads as "no narrowing", not "an empty selection".
  if ("sourceKind" in details) {
    if (details.sourceKind === "connectors") {
      if (details.selectedSourceIds.length) {
        request.sources = details.selectedSourceIds;
      }
    } else if (details.selectedModelId) {
      request.data_model_id = details.selectedModelId;
    }
  }

  return request;
};

const storyPath = (workspaceId: string, storyId: string, tail: string): string =>
  `/workspace/${workspaceId}/story/${storyId}/${tail}`;

/**
 * Where the app goes once a story has been created, or `null` when the create
 * response gives it nowhere to go.
 *
 * A story with a plan continues on the plan screen. A new-path `blank` story
 * has no plan by design (`story_plan_id: null`) and is ready on creation, so it
 * goes straight to the build page. Any other response without a plan is a
 * failed create, which the caller reports instead of navigating.
 */
export const createdStoryPath = (
  workspaceId: string,
  { creation_method }: CreateStoryRequest,
  { story_id, story_plan_id }: CreateStoryResponse,
): string | null => {
  if (story_plan_id) return storyPath(workspaceId, story_id, `plan/${story_plan_id}`);
  return creation_method === "blank"
    ? storyPath(workspaceId, story_id, "build")
    : null;
};

/**
 * The statuses of a blank create that did not finish: Story Manager was slow
 * (504), or the workflow engine would not start its edit (503). Both carry the
 * generic error code `internal_error`, so only the status tells them from any
 * other failure.
 */
const UNFINISHED_CREATE_STATUSES: ReadonlyArray<number> = [503, 504];

const isUnfinishedBlankCreate = (
  { creation_method }: CreateStoryRequest,
  error: unknown,
): boolean => {
  const status = getApiErrorStatus(error);
  return (
    creation_method === "blank" &&
    status !== null &&
    UNFINISHED_CREATE_STATUSES.includes(status)
  );
};

/**
 * The story an unfinished blank create left behind, when the API names it in
 * the error `details`. Absent (no details, `details: {}`, or no usable id)
 * means nothing was left behind.
 */
const leftBehindStoryId = (
  request: CreateStoryRequest,
  error: unknown,
): string | null => {
  if (!isUnfinishedBlankCreate(request, error)) return null;
  const storyId = getApiErrorDetailsRecord(error)?.story_id;
  return typeof storyId === "string" && storyId ? storyId : null;
};

/**
 * Where the app goes after a create that failed, or `null` to stay put and
 * report it.
 *
 * A blank create waits on Story Manager (up to about 20 s). When it does not
 * finish, 504 or 503, the story may already exist, and the API says so by
 * putting its id in the error `details`: it is ready to build on, so go there
 * rather than make the user retry into a second one. Without an id nothing was
 * left behind, and the caller reports a retry instead.
 */
export const createdStoryPathAfterError = (
  workspaceId: string,
  request: CreateStoryRequest,
  error: unknown,
): string | null => {
  const storyId = leftBehindStoryId(request, error);
  return storyId ? storyPath(workspaceId, storyId, "build") : null;
};

/** What a blank create that never finished tells the user. */
export const CREATE_STORY_RETRY_MESSAGE =
  "The story could not be created right now. Please try again.";

/**
 * The text for a failed create. A blank create that did not finish and left no
 * story is a retry. One that left a story is never a retry (a second try would
 * make a second story; `createdStoryPathAfterError` opens the first), and any
 * other failure says what the API said.
 */
export const createStoryErrorMessage = (
  request: CreateStoryRequest,
  error: unknown,
): string =>
  isUnfinishedBlankCreate(request, error) && !leftBehindStoryId(request, error)
    ? CREATE_STORY_RETRY_MESSAGE
    : getApiErrorMessage(error, "The story couldn't be created. Try again.");
